<section class="go-container">

  <go-card class="go-column go-column--100" id="usage">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Usage</h2>
      <go-copy cardId="usage" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy go-body-copy--no-margin">
          The <code class="code-block--inline">&lt;go-table&gt;</code> component should be used for any instance
          of a data table throughout the app.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h3 class="go-heading-3">Bindings</h3>
        <code [highlight]="componentBindings"></code>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">loadingData</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          This binding can be used to apply a loading state to the table.
          Typically, this should only be used for server-side integration while awaiting data.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">maxHeight</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          This binding can be used to specify a maximum height of the table content.
          By default the table will grow to fit the size of its content. A pixel value should be passed in to override this.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">minHeight</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          This binding can be used to specify a minimum height of the table content. Any value supported by the
          <code class="code-block--inline">min-height</code> CSS property may be used. By default there is no minimum height.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">renderBoxShadows</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          This binding toggles box-shadows for the table. By default, it is set to <code class="code-block--inline">true</code>.
          Typically used for embedding a table within an accordion.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">showTableActions</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          This binding enables the container for table actions. By default, it is set to <code class="code-block--inline">false</code>.
          Additional details can be found within the "Implementing Table Actions" section.
        </p>
      </div>
      
      <div class="go-column go-column--100">
        <h4 class="go-heading-4">stickyHeader</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          This property enables sticky column headers on the table, this is optional and defaults to false.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">stickyHeader</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          This property enables sticky column headers on the table, this is optional and defaults to false.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">tableConfig</h4>
        <p class="go-body-copy">
          This is a class to represent the entire table data and its configurations.
          The only required property is <code class="code-block--inline">tableData</code>.
        </p>
        <p class="go-body-copy go-body-copy--no-margin">
          It has these properties:
          <code [highlight]="tableConfigClass"></code>
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">tableTitle</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          This property adds a title above the table, this is optional.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <h4 class="go-heading-4">wrapContent</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          This binding handles how the content of the table cells wrap. By default, content is wrapped. Note that the 
          <code class="code-block--inline">go-table-column</code> component also has this binding and will override the parent table's
          value for that individual column.
        </p>
      </div>

    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="default">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Default Usage</h2>
      <go-copy cardId="default" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <h4 class="go-heading-4">example.html</h4>
      <code [highlight]="basicExample_html"></code>
      <h4 class="go-heading-4">example.ts</h4>
      <code [highlight]="basicExample_ts"></code>
    </ng-container>
    <p class="go-body-copy">
      Additionally, you can specify a type for the tableData items.
    </p>
    <code [highlight]="filterTableEx"></code>
  </go-card>

  <go-table class="go-column go-column--100" [tableConfig]="tableConfig" tableTitle="Default Example">
    <go-table-column field="id" title="ID"></go-table-column>
    <go-table-column field="name.first" title="First Name"></go-table-column>
    <go-table-column field="name.last" title="Last Name"></go-table-column>
    <go-table-column field="email" title="Email"></go-table-column>
    <go-table-column field="gender" title="Gender"></go-table-column>
    <go-table-column field="ip_address" title="IP Address"></go-table-column>
  </go-table>

  <go-card class="go-column go-column--100" id="no-box-shadow">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">No Box-Shadow Usage</h2>
      <go-copy cardId="no-box-shadow" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <h4 class="go-heading-4">example.html</h4>
      <code [highlight]="boxShadowExample_html"></code>
      <h4 class="go-heading-4">example.ts</h4>
      <code [highlight]="basicExample_ts"></code>
    </ng-container>
  </go-card>

  <go-table class="go-column go-column--100" [tableConfig]="tableConfig" tableTitle="No Box-Shadow Example" [renderBoxShadows]="false">
    <go-table-column field="id" title="ID"></go-table-column>
    <go-table-column field="name.first" title="First Name"></go-table-column>
    <go-table-column field="name.last" title="Last Name"></go-table-column>
    <go-table-column field="email" title="Email"></go-table-column>
    <go-table-column field="gender" title="Gender"></go-table-column>
    <go-table-column field="ip_address" title="IP Address"></go-table-column>
  </go-table>

  <go-card class="go-column go-column--100" id="max-height">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Max Height</h2>
      <go-copy cardId="max-height" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <h4 class="go-heading-4">example.html</h4>
      <code [highlight]="maxHeightExample_html"></code>
      <h4 class="go-heading-4">example.ts</h4>
      <code [highlight]="basicExample_ts"></code>
    </ng-container>
  </go-card>

  <go-table
    class="go-column go-column--100"
    [tableConfig]="tableConfig"
    tableTitle="Max Height Example"
    maxHeight="300px">
    <go-table-column field="id" title="ID"></go-table-column>
    <go-table-column field="name.first" title="First Name"></go-table-column>
    <go-table-column field="name.last" title="Last Name"></go-table-column>
    <go-table-column field="email" title="Email"></go-table-column>
    <go-table-column field="gender" title="Gender"></go-table-column>
    <go-table-column field="ip_address" title="IP Address"></go-table-column>
  </go-table>

  <go-card class="go-column go-column--100" id="min-height">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Min Height</h2>
      <go-copy cardId="min-height" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <h4 class="go-heading-4">example.html</h4>
      <code [highlight]="minHeightExample_html"></code>
      <h4 class="go-heading-4">example.ts</h4>
      <code [highlight]="basicExample_ts"></code>
    </ng-container>
  </go-card>

  <go-table
      class="go-column go-column--100"
      [tableConfig]="tableConfig"
      tableTitle="Min Height Example"
      minHeight="700px">
    <go-table-column field="id" title="ID"></go-table-column>
    <go-table-column field="name.first" title="First Name"></go-table-column>
    <go-table-column field="name.last" title="Last Name"></go-table-column>
    <go-table-column field="email" title="Email"></go-table-column>
    <go-table-column field="gender" title="Gender"></go-table-column>
    <go-table-column field="ip_address" title="IP Address"></go-table-column>
  </go-table>

  <go-card class="go-column go-column--100" id="sticky-headers">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Setting the Sticky Header in Table</h2>
      <go-copy cardId="sticky-headers" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <h4 class="go-heading-4">example.html</h4>
      <code [highlight]="tableStickyConfig_html"></code>
      <h4 class="go-heading-4">example.ts</h4>
      <code [highlight]="tableStickyConfig_ts" class="code-block--no-bottom-margin"></code>
    </ng-container>
  </go-card>

  <go-table
    class="go-column go-column--100"
    [tableConfig]="tableConfig"
    tableTitle="Sticky Table Header Example"
    [showTableActions]="true"
    [stickyHeader]="stickyHeader"
    maxHeight="200px">       
    <ng-container go-table-actions>
      <go-button (handleClick)="changeStickyHeader()">
        {{ this.stickyButton }}
      </go-button>
    </ng-container>
    <go-table-column field="id" title="ID"></go-table-column>
    <go-table-column field="name.first" title="First Name"></go-table-column>
    <go-table-column field="name.last" title="Last Name"></go-table-column>
    <go-table-column field="email" title="Email"></go-table-column>
    <go-table-column field="gender" title="Gender"></go-table-column>
    <go-table-column field="ip_address" title="IP Address"></go-table-column>
  </go-table>

</section>
